(function ($) {

    const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const DEFAULT_MIN_YEAR = 1900;

    const parseYearAttr = (str) => {
        const match = /^(\d{4})-\d{2}-\d{2}$/.exec(str || '');
        return match ? Number(match[1]) : null;
    };

    const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

    const daysInMonth = (month, year) => (month === 2 && isLeapYear(year)) ? 29 : DAYS_IN_MONTH[month - 1];

    const pad2 = (n) => String(n).padStart(2, '0');

    // touch-primary devices (phones/tablets) get the OS's native date picker
    // instead of the custom widget, which is built for mouse + keyboard use
    const isTouchDevice = () => window.matchMedia('(pointer: coarse)').matches;

    const ICONS = {
        calendar: '<svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.375 6H13.625C13.8125 6 14 6.1875 14 6.375V14.5C14 15.3438 13.3125 16 12.5 16H1.5C0.65625 16 0 15.3438 0 14.5V6.375C0 6.1875 0.15625 6 0.375 6ZM14 4.625C14 4.84375 13.8125 5 13.625 5H0.375C0.15625 5 0 4.84375 0 4.625V3.5C0 2.6875 0.65625 2 1.5 2H3V0.375C3 0.1875 3.15625 0 3.375 0H4.625C4.8125 0 5 0.1875 5 0.375V2H9V0.375C9 0.1875 9.15625 0 9.375 0H10.625C10.8125 0 11 0.1875 11 0.375V2H12.5C13.3125 2 14 2.6875 14 3.5V4.625Z" fill="#FFB86E"/></svg>',
        chevronUp: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>',
        chevronDown: '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>'
    };

    const SEGMENTS = ['month', 'day', 'year'];

    const buildWidgetMarkup = (isDisabled = false) => {
        
        const segmentsHtml = SEGMENTS.map((key) => `
            <div class="date-picker--segment" data-segment="${key}">
                <button type="button" class="date-picker--stepper date-picker--stepper--up" data-step="up" aria-label="Increase ${key}">${ICONS.chevronUp}</button>
                <span class="date-picker--value" data-segment-value role="spinbutton" tabindex="0" aria-label="${key}"></span>
                <button type="button" class="date-picker--stepper date-picker--stepper--down" data-step="down" aria-label="Decrease ${key}">${ICONS.chevronDown}</button>
            </div>`).join('');

        return `
            <div class="date-picker ${(isDisabled)?'disabled':''}" data-date-picker>
                <div class="date-picker--field">
                    <button type="button" class="date-picker--trigger" data-date-trigger>
                        <span class="date-picker--display" data-date-display>mm/dd/yyyy</span>
                    </button>
                    <button type="button" class="date-picker--icon-btn" data-date-icon-btn aria-haspopup="true" aria-expanded="false" aria-label="Open date picker">
                        ${ICONS.calendar}
                    </button>
                </div>
                <div class="date-picker--panel" data-date-panel role="group" aria-label="Date of birth">
                    ${segmentsHtml}
                </div>
            </div>`;
    };

    class DatePicker {
        constructor(input) {
            this.input = input;
            this.input.classList.add('date-picker--native-input');
            this.isTouch = isTouchDevice();
            this.hasValue = false;
            this.buffers = { month: '', day: '', year: '' };
            this.timers = {};
            this.minYearOverride = parseYearAttr(input.min);
            this.maxYearOverride = parseYearAttr(input.max);

            const today = new Date();
            this.value = { month: today.getMonth() + 1, day: today.getDate(), year: today.getFullYear() };

            const [y, m, d] = (input.value || '').split('-').map(Number);
            if (y && m && d) {
                this.value = { month: m, day: d, year: y };
                this.hasValue = true;
            }

            input.insertAdjacentHTML('afterend', buildWidgetMarkup(input.disabled));
            this.el = input.nextElementSibling;
            this.trigger = this.el.querySelector('[data-date-trigger]');
            this.iconBtn = this.el.querySelector('[data-date-icon-btn]');
            this.display = this.el.querySelector('[data-date-display]');

            this.segments = {};
            this.el.querySelectorAll('[data-segment]').forEach((segEl) => {
                this.segments[segEl.dataset.segment] = segEl.querySelector('[data-segment-value]');
            });

            if (this.isTouch) {
                // move the real input inside the visual field so it can be sized
                // to exactly that box (trigger + icon), not the whole label+field
                // wrapper - keeps its overlay tight instead of spanning too far
                this.el.querySelector('.date-picker--field').appendChild(this.input);
            }

            this.renderSegments();
            this.renderDisplay();
            this.bindEvents();
        }

        minYear() {
            return this.minYearOverride || DEFAULT_MIN_YEAR;
        }

        maxYear() {
            return this.maxYearOverride || new Date().getFullYear();
        }

        renderSegments() {
            this.segments.month.textContent = pad2(this.value.month);
            this.segments.day.textContent = pad2(this.value.day);
            this.segments.year.textContent = String(this.value.year);

            this.segments.month.setAttribute('aria-valuenow', this.value.month);
            this.segments.month.setAttribute('aria-valuemin', 1);
            this.segments.month.setAttribute('aria-valuemax', 12);

            this.segments.day.setAttribute('aria-valuenow', this.value.day);
            this.segments.day.setAttribute('aria-valuemin', 1);
            this.segments.day.setAttribute('aria-valuemax', daysInMonth(this.value.month, this.value.year));

            this.segments.year.setAttribute('aria-valuenow', this.value.year);
            this.segments.year.setAttribute('aria-valuemin', this.minYear());
            this.segments.year.setAttribute('aria-valuemax', this.maxYear());
        }

        renderDisplay() {
            this.display.textContent = this.hasValue
                ? `${pad2(this.value.month)}/${pad2(this.value.day)}/${this.value.year}`
                : 'mm/dd/yyyy';
        }

        // opening the picker commits its (initially today's-date) value immediately,
        // since a spinner has no way to represent "empty" once shown
        commit() {
            this.hasValue = true;
            this.input.value = `${this.value.year}-${pad2(this.value.month)}-${pad2(this.value.day)}`;
            this.input.dispatchEvent(new Event('change', { bubbles: true }));
            this.renderDisplay();
        }

        step(key, dir) {
            if (key === 'month') {
                this.value.month = ((this.value.month - 1 + dir + 12) % 12) + 1;
            } else if (key === 'day') {
                const max = daysInMonth(this.value.month, this.value.year);
                this.value.day = ((this.value.day - 1 + dir + max) % max) + 1;
            } else if (key === 'year') {
                this.value.year = Math.min(this.maxYear(), Math.max(this.minYear(), this.value.year + dir));
            }

            const maxDay = daysInMonth(this.value.month, this.value.year);
            if (this.value.day > maxDay) {
                this.value.day = maxDay;
            }

            this.renderSegments();
            this.commit();
        }

        armBufferTimeout(key) {
            clearTimeout(this.timers[key]);
            this.timers[key] = setTimeout(() => {
                this.buffers[key] = '';
                this.renderSegments();
            }, 1200);
        }

        focusNextSegment(key) {
            const next = { month: 'day', day: 'year', year: null }[key];
            if (next) {
                this.segments[next].focus();
            }
        }

        finalizeDigitValue(key, val, advance = true) {
            clearTimeout(this.timers[key]);
            this.buffers[key] = '';
            this.value[key] = val;
            if (key === 'month' || key === 'year') {
                const maxDay = daysInMonth(this.value.month, this.value.year);
                if (this.value.day > maxDay) {
                    this.value.day = maxDay;
                }
            }
            this.renderSegments();
            this.commit();
            if (advance) {
                this.focusNextSegment(key);
            }
        }

        handleDigit(key, digit) {
            if (key === 'year') {
                this.handleYearDigit(digit);
                return;
            }

            const max = key === 'month' ? 12 : daysInMonth(this.value.month, this.value.year);
            const buf = this.buffers[key];

            if (!buf) {
                const d = Number(digit);
                if (d === 0) {
                    this.buffers[key] = '0';
                    this.segments[key].textContent = '00';
                    this.armBufferTimeout(key);
                    return;
                }
                if (d * 10 > max) {
                    this.finalizeDigitValue(key, d);
                    return;
                }
                this.buffers[key] = digit;
                this.segments[key].textContent = pad2(d);
                this.armBufferTimeout(key);
                return;
            }

            const combined = Number(buf + digit);
            const clamped = Math.min(max, Math.max(1, combined));
            this.finalizeDigitValue(key, clamped);
        }

        handleYearDigit(digit) {
            let buf = (this.buffers.year || '') + digit;
            if (buf.length > 4) {
                buf = digit;
            }
            this.buffers.year = buf;
            this.segments.year.textContent = buf;
            this.armBufferTimeout('year');

            if (buf.length === 4) {
                const clamped = Math.min(this.maxYear(), Math.max(this.minYear(), Number(buf)));
                this.finalizeDigitValue('year', clamped);
            }
        }

        handleBackspace(key) {
            if (this.buffers[key]) {
                clearTimeout(this.timers[key]);
                this.buffers[key] = '';
                this.renderSegments();
            }
        }

        isOpen() {
            return this.el.classList.contains('date-picker--open');
        }

        open() {
            this.el.classList.add('date-picker--open');
            this.iconBtn.setAttribute('aria-expanded', 'true');
            this.segments.month.focus();
            if (!this.hasValue) {
                this.commit();
            }
        }

        close() {
            this.el.classList.remove('date-picker--open');
            this.iconBtn.setAttribute('aria-expanded', 'false');
        }

        syncFromInput() {
            const [y, m, d] = (this.input.value || '').split('-').map(Number);
            if (!(y && m && d)) {
                return;
            }

            // native pickers don't reliably enforce the min/max attribute on the
            // year alone, so clamp it the same way the custom widget already does
            const clampedYear = Math.min(this.maxYear(), Math.max(this.minYear(), y));
            this.value = { month: m, day: d, year: clampedYear };
            this.hasValue = true;
            this.renderSegments();
            this.renderDisplay();

            if (clampedYear !== y) {
                this.commit();
            }
        }

        bindEvents() {
            // on touch devices the native input is CSS-overlaid on top of the
            // whole field (see .date-picker--native-input touch styles), so taps
            // land on it directly and the browser opens its own picker natively -
            // nothing to wire up here beyond letting that default action happen
            if (!this.isTouch) {
                const activate = (e) => {
                    e.preventDefault();
                    this.isOpen() ? this.close() : this.open();
                };

                // clicking a <label for="..."> pointing at the (visually hidden)
                // native input forwards a click event here, not to the widget
                [this.trigger, this.iconBtn, this.input].forEach((control) => {
                    control.addEventListener('click', activate);
                });
            }

            this.input.addEventListener('change', () => this.syncFromInput());

            this.el.querySelectorAll('[data-segment]').forEach((segEl) => {
                const key = segEl.dataset.segment;

                segEl.querySelectorAll('.date-picker--stepper').forEach((btn) => {
                    btn.addEventListener('click', (e) => {
                        e.preventDefault();
                        this.step(key, btn.dataset.step === 'up' ? 1 : -1);
                    });
                });

                const valueEl = segEl.querySelector('[data-segment-value]');

                valueEl.addEventListener('keydown', (e) => {
                    if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        this.step(key, 1);
                    } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        this.step(key, -1);
                    } else if (/^[0-9]$/.test(e.key)) {
                        e.preventDefault();
                        this.handleDigit(key, e.key);
                    } else if (e.key === 'Backspace' || e.key === 'Delete') {
                        e.preventDefault();
                        this.handleBackspace(key);
                    }
                });

                valueEl.addEventListener('blur', () => {
                    if (!this.buffers[key]) {
                        return;
                    }
                    if (key === 'year') {
                        const clamped = Math.min(this.maxYear(), Math.max(this.minYear(), Number(this.buffers.year)));
                        this.finalizeDigitValue('year', clamped, false);
                    } else {
                        const max = key === 'month' ? 12 : daysInMonth(this.value.month, this.value.year);
                        const clamped = Math.min(max, Math.max(1, Number(this.buffers[key])));
                        this.finalizeDigitValue(key, clamped, false);
                    }
                });
            });

            document.addEventListener('click', (e) => {
                if (this.isOpen() && e.target !== this.input && !this.el.contains(e.target)) {
                    this.close();
                }
            });

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.isOpen()) {
                    this.close();
                } else if (e.key === 'Enter' && this.isOpen() && this.el.contains(e.target)) {
                    e.preventDefault();
                    this.close();
                }
            });
        }
    }

    const init = (selector = '.enrollment--field input[type="date"]') => {
        document.querySelectorAll(selector).forEach((input) => {
            if (!input.dataset.datePickerInit) {
                input.dataset.datePickerInit = 'true';
                new DatePicker(input);
            }
        });
    };

    window.YuviwelDatePicker = { init };

})(jQuery);
